import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  Button,
  Grid,
  Checkbox,
  FormControlLabel,
  Fade,
} from "@mui/material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";

const SAUCES_LIMIT = 2;

export default function CustomMealPopUp({
  open,
  handleClose,
  addToCart,
  existingItem,
  isEditing,
  onUpdate,
  products,
}) {
  const { t } = useTranslation();
  const direction = i18n.dir(i18n.language);

  const [step, setStep] = useState(1);
  const [selectedProtein, setSelectedProtein] = useState(null);
  const [selectedCarb, setSelectedCarb] = useState(null);
  const [selectedVeggies, setSelectedVeggies] = useState([]);
  const [selectedSauces, setSelectedSauces] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);

  const baseProtein = (products || []).filter((p) => p.type === "protein");
  const baseCarbs = (products || []).filter((p) => p.type === "carbs");
  const veggies = (products || []).filter((p) => p.type === "veges");
  const sauces = (products || []).filter((p) => p.type === "sauce");
  const addons = (products || []).filter((p) => p.is_addon);
  const contentRef = useRef(null);
  const basePrice = 40;

  const calcCustomMealPrice = () => {
    const proteinPrice = selectedProtein?.base_price || 0;
    const carbPrice = selectedCarb?.base_price || 0;

    const veggiesPrice = selectedVeggies.reduce((sum, v) => {
      return sum + (v.base_price || 0);
    }, 0);

    const addonsPrice = selectedAddons.reduce((sum, a) => {
      return sum + (a.addon_price || 0);
    }, 0);

    const saucesPrice = selectedSauces.reduce(
      (sum, s) => sum + (s.base_price ?? s.price ?? 0),
      0
    );

    return (
      basePrice +
      saucesPrice +
      proteinPrice +
      carbPrice +
      veggiesPrice +
      addonsPrice
    );
  };
  useEffect(() => {
    // use rAF to wait till the new step DOM is painted
    requestAnimationFrame(() => {
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: 0, behavior: "smooth" }); // or "auto"
      }
    });
  }, [step, open]);

  const customHandleOnClose = () => {
    handleClose();
  };

  const FractionStepImage = ({ step }) => (
    <img
      src={`/${step}out3.svg`}
      alt={t("stepOf3", { step })}
      style={{ height: 36, color: "#0f4d28" }}
    />
  );

  const renderOption = (item, selected, onSelect) => {
    const isSelected = selected?.id === item.id;
    const currentLang = i18n.language || "ar";
    return (
      <Box
        key={item.id}
        onClick={() => {
          setSelectedAddons([]);
          onSelect(item);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1,
          my: 1,
          borderRadius: 2,
          backgroundColor: isSelected ? "#eaf7ef" : "#fff",
          boxShadow: isSelected ? "0 0 0 2px #0f4d28" : "none",
          cursor: "pointer",
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <img
            src={item.img_url}
            alt={item.name?.[currentLang]}
            width={45}
            height={45}
            style={{ borderRadius: 8, objectFit: "cover" }}
          />
          <Typography fontFamily="Tajawal">
            {item.name?.[currentLang] || "-"}
          </Typography>
          <Typography fontFamily="Tajawal" fontSize="0.85rem" color="#999">
            {item.weight || "-"} {t("sgram")}
          </Typography>
        </Box>
        <Box textAlign="center">
          <Typography
            fontSize="0.75rem"
            color="#999"
            fontFamily="Tajawal"
            mb={0.2}
          >
            {t("price")}
          </Typography>
          <Typography
            fontWeight="bold"
            fontFamily="Tajawal"
            color={isSelected ? "#0f4d28" : "#777"}
          >
            {item.base_price ?? item.price} ₪
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderMultiSelect = (
    items,
    selectedList,
    setSelectedList,
    isAddon = false,
    maxSelections = -1
  ) => (
    <Grid container direction="column">
      {items.map((item) => {
        const isSelected = selectedList.some((v) => v.id === item.id);
        const atLimit =
          maxSelections >= 0 && selectedList.length >= maxSelections;
        const disabled = !isSelected && atLimit;
        const currentLang = i18n.language || "ar";

        const toggle = () => {
          if (disabled && !isSelected) return;
          if (isSelected) {
            setSelectedList((prev) => prev.filter((v) => v.id !== item.id));
          } else if (!disabled) {
            setSelectedList((prev) => [...prev, item]);
          }
        };

        return (
          <Box
            key={item.id}
            onClick={toggle}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1,
              my: 1,
              borderRadius: 2,
              backgroundColor: isSelected ? "#eaf7ef" : "#fff",
              boxShadow: isSelected ? "0 0 0 2px #0f4d28" : "none",
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Checkbox
                checked={isSelected}
                onChange={(e) => {
                  if (e.target.checked) {
                    if (!disabled) {
                      setSelectedList((prev) =>
                        prev.some((v) => v.id === item.id)
                          ? prev
                          : [...prev, item]
                      );
                    }
                  } else {
                    setSelectedList((prev) =>
                      prev.filter((v) => v.id !== item.id)
                    );
                  }
                }}
                disabled={disabled}
                icon={
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "6px",
                      backgroundColor: "#fff",
                      border: "2px solid #ccc",
                    }}
                  />
                }
                checkedIcon={
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "6px",
                      backgroundColor: "#0f4d28",
                      border: "2px solid #ccc",
                    }}
                  />
                }
                sx={{ p: 0.5 }}
              />

              <img
                src={item.img_url}
                alt={item.name?.[currentLang]}
                width={45}
                height={45}
                style={{ borderRadius: 8, objectFit: "cover" }}
              />
              <Typography fontFamily="Tajawal">
                {item.name?.[currentLang] || "-"}
              </Typography>

              {step === 3 && (
                <Typography
                  fontFamily="Tajawal"
                  fontSize="0.85rem"
                  color="#999"
                  sx={{ whiteSpace: "nowrap" }}
                  pr={1}
                >
                  {item.weight || "-"} {t("sgram")}
                </Typography>
              )}
            </Box>

            <Box textAlign="center">
              <Typography
                fontSize="0.75rem"
                color="#999"
                fontFamily="Tajawal"
                mb={0.2}
              >
                {t("price")}
              </Typography>
              <Typography
                fontWeight="bold"
                fontFamily="Tajawal"
                color={isSelected ? "#0f4d28" : "#777"}
              >
                {isAddon
                  ? item.addon_price ?? item.price
                  : item.base_price ?? item.price}{" "}
                ₪
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Grid>
  );

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            <Typography textAlign="center" fontFamily="Tajawal" mb={2}>
              {t("chooseOne")}
            </Typography>
            <Typography
              fontWeight="bold"
              fontSize="1rem"
              fontFamily="Tajawal"
              mb={1}
              color="#0f4d28"
            >
              {t("chooseProtein")}
            </Typography>
            {baseProtein.map((item) =>
              renderOption(item, selectedProtein, setSelectedProtein)
            )}

            <Typography
              fontWeight="bold"
              fontSize="1rem"
              fontFamily="Tajawal"
              mb={1}
              color="#0f4d28"
            >
              {t("chooseCarbs")}
            </Typography>
            {baseCarbs.map((item) =>
              renderOption(item, selectedCarb, setSelectedCarb)
            )}
          </>
        );
      case 2:
        return (
          <>
            <Typography textAlign="center" fontFamily="Tajawal" mb={2}>
              {`${t("chooseVeggiesOptional")} (${t("upTo100g")})`}
            </Typography>
            {renderMultiSelect(veggies, selectedVeggies, setSelectedVeggies)}

            {sauces && sauces.length > 0 && (
              <Typography textAlign="center" fontFamily="Tajawal" mb={2} mt={2}>
                {`${t("chooseSauceOptional")}   ${t("upTo", {
                  upTo: SAUCES_LIMIT - selectedSauces?.length || 0,
                })}`}
              </Typography>
            )}

            {renderMultiSelect(
              sauces,
              selectedSauces,
              setSelectedSauces,
              false,
              SAUCES_LIMIT
            )}
          </>
        );
      case 3: {
        // Keep only relevant add‑ons (by selected protein/carb categories or general 0)
        const filteredAddons = addons.filter(
          (a) =>
            (selectedProtein &&
              a.category_id === selectedProtein.category_id) ||
            (selectedCarb && a.category_id === selectedCarb.category_id) ||
            a.category_id === 0
        );

        // Desired order
        const ORDER = ["protein", "carbs", "veges", "sauces"];

        // Types present, in the order we want
        const addonTypes = ORDER.filter((t) =>
          filteredAddons.some((a) => a.type === t)
        );

        const labelForType = (type) => {
          if (type === "sauces") return t("sauces");
          if (type === "carbs") return t("extraCarbs");
          if (type === "protein") return t("extraProtein");
          if (type === "veges") return t("extraVeggies");
          return type;
        };
        return (
          <>
            <Typography textAlign="center" fontFamily="Tajawal" mb={2}>
              {t("addonsOptional")}
            </Typography>
            {addonTypes.map((type) => (
              <Box key={type} mt={2}>
                <Typography
                  fontWeight="bold"
                  fontSize="1rem"
                  fontFamily="Tajawal"
                  mb={1}
                  color="#0f4d28"
                >
                  {labelForType(type)}
                </Typography>

                {renderMultiSelect(
                  // (optional) sort items inside each section by Arabic name
                  filteredAddons
                    .filter((a) => a.type === type)
                    .sort((a, b) =>
                      (a.name?.ar || "").localeCompare(b.name?.ar || "", "ar")
                    ),
                  selectedAddons,
                  setSelectedAddons,
                  true
                )}
              </Box>
            ))}
          </>
        );
      }

      default:
        return null;
    }
  };
  useEffect(() => {
    if (isEditing && existingItem && products.length > 0) {
      setSelectedProtein(existingItem.components?.protein || null);
      setSelectedCarb(existingItem.components?.carb || null);
      setSelectedVeggies(existingItem.components?.veggies || []);
      setSelectedSauces(existingItem.components?.sauces || []);
      setSelectedAddons(existingItem.components?.addons || []);
    }
  }, [isEditing, existingItem, products]);
  if (!products || !Array.isArray(products) || products.length === 0) {
    return null;
  }
  return (
    <Dialog
      open={open}
      onClose={customHandleOnClose}
      fullWidth
      dir={direction}
      slots={{ transition: Fade }}
      slotProps={{
        transition: {
          onExited: () => {
            setStep(1);
            setSelectedProtein(null);
            setSelectedCarb(null);
            setSelectedVeggies([]);
            setSelectedSauces([]);
            setSelectedAddons([]);
          },
        },
        paper: {
          sx: {
            borderRadius: 12,
            overflow: "hidden",
            maxHeight: "70%",
            margin: "auto", // center horizontally
            display: "flex",
            flexDirection: "column",
            justifyContent: "center", // center vertically
          },
        },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={4}
      >
        {/* Close Icon */}
        <Box width="24px" textAlign="right">
          <IconButton onClick={customHandleOnClose} size="small">
            <HighlightOffIcon />
          </IconButton>
        </Box>

        {/* Title */}
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <DialogTitle
            sx={{
              textAlign: "center",
              flexGrow: 1,
              fontSize: "clamp(1.3rem, 1.5vw, 1.8rem)",
              fontWeight: "bold",
              color: "#0f4d28",
              fontFamily: "Tajawal",
            }}
          >
            {t("buildYourMeal")}
          </DialogTitle>
          <FractionStepImage step={step} />
        </Box>

        {/* Back Arrow – show only on step > 1 */}
        <Box width="24px">
          {step > 1 && (
            <IconButton
              onClick={() => setStep((prev) => prev - 1)}
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      {step > 0 && (
        <Typography
          sx={{
            fontWeight: "bold",
            color: "#0f4d28",
            fontFamily: "Tajawal",
            fontSize: "1rem",
            whiteSpace: "nowrap",
            textAlign: "center",
            mb: "0.75rem",
          }}
        >
          {t("rate")} {calcCustomMealPrice()} ₪
        </Typography>
      )}
      <Box width="60%" borderBottom="1px solid #999" mx="auto" />
      <DialogContent
        ref={contentRef}
        sx={{
          pb: 0,
          // make it scrollable
          overflowY: "auto",
          // set a predictable max height so it actually scrolls
          maxHeight: { xs: "60vh", sm: "65vh" },
        }}
      >
        {renderStepContent()}
      </DialogContent>

      <Box textAlign="center" borderTop={0.2} color="#999">
        <Button
          onClick={() => {
            if (step === 1 && selectedProtein && selectedCarb) {
              setStep(2);
            } else if (step === 2) {
              setStep(3);
            } else {
              const item = {
                ...existingItem,
                name: t("customMealName"),
                isCustom: true,
                price: calcCustomMealPrice(),
                qty: 1,
                imageUrl: "/customizedMeal.png",
                components: {
                  protein: selectedProtein,
                  carb: selectedCarb,
                  veggies: selectedVeggies,
                  addons: selectedAddons,
                  sauces: selectedSauces,
                },
              };

              if (isEditing && onUpdate) {
                onUpdate(existingItem.id, item);
              } else {
                const id = `custom-${Date.now()}-${Math.floor(
                  Math.random() * 1000
                )}`;
                addToCart({ ...item, id });
              }

              customHandleOnClose();
            }
          }}
          sx={{
            backgroundColor: step === 3 ? "#0f4d28" : "#eaf7ef",
            color: step === 3 ? "white" : "#0f4d28",
            fontFamily: "Tajawal",
            fontSize: "1.2rem",
            borderRadius: 0,
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
            width: "100%",
            py: 1.5,
          }}
          disabled={step === 1 && (!selectedProtein || !selectedCarb)}
        >
          {step === 3 ? t("addToCart") : t("next")}
        </Button>
      </Box>
    </Dialog>
  );
}
